import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { NgStyle } from '@angular/common';
import { CropUploadSettings, UploadedFile } from '@bo/ng-uploads';
import { UploadedFileWithMeta } from '../../utils/upload-utils';
import { CropUploadPaysComponent } from '../upload/crop-upload/crop-upload.component';
import {
  BarcodeValidationResult,
  BarcodeValidationStatus,
  DocumentType,
} from '../../interfaces/barcode-validation.interface';
import { BarcodeValidatorComponent } from '../barcode-validator/barcode-validator.component';

@Component({
  selector: 'app-upload-img-crop',
  imports: [NgStyle, CropUploadPaysComponent, BarcodeValidatorComponent],
  templateUrl: './upload-img-crop.component.html',
  styleUrl: './upload-img-crop.component.scss',
})
export class UploadImgCropComponent implements AfterViewInit, OnChanges {
  @Input() width: number | undefined;
  @Input() height: number | undefined;
  @Input() title: string | undefined;
  @Input() text: string | undefined;
  @Input() destination: string | undefined;
  @Input() fieldId: string | undefined;
  @Input() enableBarcodeValidation: boolean = false;
  @Input() documentType: DocumentType = 'id';

  @Output() imageStored = new EventEmitter<string>();
  @Output() barcodeValidationResult = new EventEmitter<BarcodeValidationResult>();
  @Output() barcodeValidationStatusChange = new EventEmitter<BarcodeValidationStatus>();

  @ViewChild(BarcodeValidatorComponent) barcodeValidator?: BarcodeValidatorComponent;

  cropperSettings: CropUploadSettings | undefined;
  errors: string[] | null | undefined;
  previewUrl: string | undefined;
  uploadedImageUrl: string | undefined; // URL of the uploaded image from localstack

  ngAfterViewInit(): void {
    this.initializeCropperSettings();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['width'] || changes['height'] || changes['destination']) {
      this.initializeCropperSettings();
    }
  }

  private initializeCropperSettings(): void {
    this.cropperSettings = {
      destination: this.destination,
      fileOwner: {},
      width: this.width,
      height: this.height,
      rounded: false,
      filename: 'EmployeeId',
    } as CropUploadSettings;
  }

  pictureUploaded($event: UploadedFile) {
    this.errors = null;
    // file.meta is appended to file as part of the BO upload library
    // tslint:disable-next-line:variable-name
    const _event = $event as UploadedFileWithMeta;
    this.previewUrl = _event.file?.meta?.download_url;

    if (this.previewUrl) {
      this.imageStored.emit(this.previewUrl);

      // Trigger barcode validation AFTER upload if enabled
      if (this.enableBarcodeValidation) {
        this.uploadedImageUrl = this.previewUrl;
        // Trigger validation manually after a short delay to ensure ViewChild is available
        setTimeout(() => {
          this.barcodeValidator?.validateBarcode();
        }, 100);
      }
    }
  }

  onChange() {
    this.previewUrl = undefined;
    this.uploadedImageUrl = undefined;

    // Reset barcode validation when image is removed
    if (this.enableBarcodeValidation) {
      this.barcodeValidationResult.emit({
        isValid: false,
        error: {
          code: 'INVALID_IMAGE',
          message: 'Image removed',
          details: 'Please upload a new image',
        },
      });
      this.barcodeValidationStatusChange.emit(BarcodeValidationStatus.IDLE);
    }

    this.imageStored.emit();
  }

  /**
   * Pass through barcode validation result
   */
  onBarcodeValidationResult(result: BarcodeValidationResult): void {
    this.barcodeValidationResult.emit(result);
  }

  /**
   * Pass through barcode validation status change
   */
  onBarcodeValidationStatusChange(status: BarcodeValidationStatus): void {
    this.barcodeValidationStatusChange.emit(status);
  }
}
